import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Focus,
  Brain,
  Users,
  Workflow,
  LineChart,
  Wrench,
} from "lucide-react";

interface QualificationProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefit: string;
}

export default function QualificationAccordion() {
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  const qualifications: QualificationProps[] = [
    {
      icon: <Focus className="h-8 w-8 text-gray-900" />,
      title: "Limited Client Base for Maximum Focus",
      description:
        "At One80, we intentionally limit the number of clients we work with to provide each business with personalized attention. Your projects won't get lost in the shuffle.",
      benefit:
        "Partner with a PM who gives your business the focus it deserves.",
    },
    {
      icon: <Brain className="h-8 w-8 text-gray-900" />,
      title: "Advanced AI Expertise",
      description:
        "We don't just understand AI—we live it. Our PMs have hands-on experience with AI technologies and know how to apply them strategically to automate workflows, enhance productivity, and optimize project outcomes.",
      benefit:
        "Learn how AI-driven project management can improve your productivity.",
    },
    {
      icon: <Users className="h-8 w-8 text-gray-900" />,
      title: "Experts in Customer-Centric Design",
      description:
        "Innovation doesn't happen in a vacuum. Our approach emphasizes customer-centric design principles. We collaborate with you to build, test, and refine prototypes that drive real-world outcomes.",
      benefit: "Deliver solutions your customers will love.",
    },
    {
      icon: <Workflow className="h-8 w-8 text-gray-900" />,
      title: "Effective Automation Solutions",
      description:
        "We know that automation is more than a buzzword. By utilizing AI, we create custom automation solutions tailored to your business. From streamlining processes to automating project updates and reporting, our PMs help reduce time-consuming manual work by up to 30%.",
      benefit: "See how automation can save time and resources.",
    },
    {
      icon: <LineChart className="h-8 w-8 text-gray-900" />,
      title: "Data Expertise for Strategic Decision-Making",
      description:
        "Data-driven decisions are critical to success. We're experts at working with data, analyzing trends, and generating actionable reports. Our AI-powered PMs provide insights that empower faster, smarter decisions, giving your business a competitive edge.",
      benefit: "Transform your data into insights that drive growth.",
    },
    {
      icon: <Wrench className="h-8 w-8 text-gray-900" />,
      title: "Broad Technical Experience Across Industries",
      description:
        "One80's team brings a diverse range of technical experience, including full-stack development, Software as a Service (SaaS), software administration, training, change management, and product discovery. This versatility allows us to guide projects through every stage of development and implementation.",
      benefit: "Technology evolves quickly—your projects should too.",
    },
  ];

  return (
    <div className="space-y-4">
      {qualifications.map((qual, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg overflow-hidden bg-white transition-all duration-300 hover:shadow-md"
        >
          <button
            onClick={() =>
              setExpandedItem(expandedItem === index ? null : index)
            }
            className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="flex items-center space-x-4">
              <div className="bg-gray-100 p-3 rounded-lg">{qual.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900">
                {qual.title}
              </h3>
            </div>
            {expandedItem === index ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          <div
            className={`px-6 overflow-hidden transition-all duration-300 ${
              expandedItem === index ? "max-h-96 py-4" : "max-h-0"
            }`}
          >
            <p className="text-gray-600 mb-4">{qual.description}</p>
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-[#F98B3D] mt-1 mr-2 flex-shrink-0" />
              <p className="text-[#F98B3D]">{qual.benefit}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
